import { useMemo, useRef, useState, type ReactNode } from "react";
import { fromControl } from "./dom.js";
import { EmptyState, Spinner } from "./primitives.js";

export interface Column<T> {
  key: string;
  header: ReactNode;
  render?: (row: T) => ReactNode;
  align?: "left" | "right" | "center";
  width?: number | string;
  sortable?: boolean;
  sortValue?: (row: T) => string | number | null;
  footer?: ReactNode;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[] | undefined;
  rowKey: (row: T) => string;
  loading?: boolean;
  empty?: ReactNode;
  onRowClick?: (row: T) => void;
  caption?: string;
  maxHeight?: number;
}

const ROW_H = 40;
const WINDOW_AFTER = 500;

/**
 * Sortable table. Above 500 rows it renders only the visible window, so 100,000-row ledgers stay smooth
 * (SOW Modules 8 and 19). Clickable rows open on a click or Enter on the row itself, never from a button, link or field
 * inside it.
 */
export function DataTable<T>({ columns, rows, rowKey, loading, empty, onRowClick, caption, maxHeight = 640 }: DataTableProps<T>) {
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 } | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const wrap = useRef<HTMLDivElement>(null);

  const sorted = useMemo(() => {
    if (!rows || !sort) return rows ?? [];
    const col = columns.find((c) => c.key === sort.key);
    const get = col?.sortValue ?? ((r: T) => (r as Record<string, unknown>)[sort.key] as string | number | null);
    return [...rows].sort((a, b) => {
      const x = get(a); const y = get(b);
      if (x === y) return 0;
      if (x === null || x === undefined) return 1;
      if (y === null || y === undefined) return -1;
      return (x > y ? 1 : -1) * sort.dir;
    });
  }, [rows, sort, columns]);

  if (loading && !rows) return <div className="tos-card tos-card-body"><Spinner /></div>;
  if (!sorted.length) return <div className="tos-card">{empty ?? <EmptyState title="Nothing to show yet" />}</div>;

  const virtual = sorted.length > WINDOW_AFTER;
  const start = virtual ? Math.max(0, Math.floor(scrollTop / ROW_H) - 10) : 0;
  const end = virtual ? Math.min(sorted.length, start + Math.ceil(maxHeight / ROW_H) + 20) : sorted.length;
  const visible = sorted.slice(start, end);
  const hasFooter = columns.some((c) => c.footer !== undefined);

  return (
    <div className="tos-table-wrap" ref={wrap} style={{ maxHeight }} onScroll={virtual ? (e) => setScrollTop(e.currentTarget.scrollTop) : undefined}>
      <table className="tos-table">
        {caption && <caption className="tos-sr-only">{caption}</caption>}
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={c.align} style={{ width: c.width }} aria-sort={sort?.key === c.key ? (sort.dir === 1 ? "ascending" : "descending") : undefined}>
                {c.sortable ? (
                  <button type="button" onClick={() => setSort((s) => (s?.key === c.key ? { key: c.key, dir: s.dir === 1 ? -1 : 1 } : { key: c.key, dir: 1 }))}>
                    {c.header} {sort?.key === c.key ? (sort.dir === 1 ? "▲" : "▼") : ""}
                  </button>
                ) : c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {virtual && start > 0 && <tr style={{ height: start * ROW_H }} aria-hidden="true" />}
          {visible.map((r) => (
            <tr key={rowKey(r)} className={onRowClick ? "tos-clickable" : undefined} tabIndex={onRowClick ? 0 : undefined}
              onClick={onRowClick ? (e) => { if (!fromControl(e.target, e.currentTarget)) onRowClick(r); } : undefined}
              onKeyDown={onRowClick ? (e) => { if (e.key === "Enter" && e.target === e.currentTarget) onRowClick(r); } : undefined}>
              {columns.map((c) => (
                <td key={c.key} className={c.align}>{c.render ? c.render(r) : String((r as Record<string, unknown>)[c.key] ?? "")}</td>
              ))}
            </tr>
          ))}
          {virtual && end < sorted.length && <tr style={{ height: (sorted.length - end) * ROW_H }} aria-hidden="true" />}
        </tbody>
        {hasFooter && (
          <tfoot><tr>{columns.map((c) => <td key={c.key} className={c.align}>{c.footer}</td>)}</tr></tfoot>
        )}
      </table>
    </div>
  );
}
